import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications, fetchApplication, postApplication, safePdf, isThunkFulfilled } from "../../../store/reducers/applicationSlice";
import { notifyApiError, notifyApiSuccess } from "../../../helpers/apiNotify";
import { getAttachments } from "../../../store/reducers/fileSlice";
import { showSuccess, showError } from '../../../helpers/toast';
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { createNotification } from '../../../store/reducers/notificationSlice';
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import { formatBahamasDate } from "../../../helpers/date";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppApprovedApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchTerm("");
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      let term = searchTerm;
      if (searchBy === "applicationId" && term.startsWith("GB-")) {
        term = term.replace("GB-", "");
      }
      setDebouncedSearchTerm(term);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, searchBy]);

  // Fetch Applications Data
  useEffect(() => {
    let queryParams = {
      state: 'approved' // Add state filter to API call
    };

    // SECURITY: Add company_id filter at API level for company_admin
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
      queryParams.user_id = user.user.id;
    }

    if (startDate) {
      queryParams.approvedFrom = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.approvedTo = moment(endDate).format("YYYY-MM-DD");
    }

    // Add search parameters if debouncedSearchTerm is present
    if (debouncedSearchTerm) {
      queryParams[searchBy] = debouncedSearchTerm;
    }

    dispatch(
      fetchApplications({
        accessToken: authToken,
        queryParams: queryParams,
        fetchAllPages: true // Fetch all pages to show all applications
      })
    );
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, user.user?.id, debouncedSearchTerm, searchBy]);

  // PERFORMANCE: Optimized merge and filter using useMemo and Map for O(1) lookups
  const mergedAndFilteredData = useMemo(() => {
    if (!applications.data?.length || !getallUserdata?.length) {
      return [];
    }

    // PERFORMANCE: Create a Map for O(1) user lookup instead of O(n) find()
    const userMap = new Map(getallUserdata.map(user => [user.id, user]));
    const userRole = user.user?.user_role;
    const currentUserId = user.user?.id;
    const userCompanyId = user.user?.company_id;

    // PERFORMANCE: Filter and merge in one pass to avoid multiple iterations
    const filteredData = applications.data
      .filter((appItem) => {
        // Early exit if state doesn't match
        if (appItem.state !== "approved") return false;

        const itemCompanyId = appItem.company_id || appItem.companyId;
        const itemUserId = appItem.user_id || appItem.userId || appItem.user?.id;

        // SECURITY: If company_id is missing, don't show the application
        if (!itemCompanyId || !userCompanyId) return false;

        // Role-based filtering
        if (userRole === "company_admin") {
          return String(itemCompanyId) === String(userCompanyId);
        } else if (userRole === "employee") {
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          return isOwnApplication && hasCorrectCompany;
        } else {
          return String(itemCompanyId) === String(userCompanyId);
        }
      })
      .map((appItem) => {
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;
        const userItem = userMap.get(userIdFromApp); // O(1) lookup instead of O(n) find()
        const finalUserId = userItem?.id || userIdFromApp;
        const finalCompanyId = appItem.company_id || appItem.companyId || userItem?.company_id || user.user?.company_id;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId,
            userId: userItem.id,
            company_id: finalCompanyId,
            created_at: appItem.created_at,
            updated_at: appItem.updated_at,
            submissionDate: appItem.submissionDate,
            approvalDate: appItem.approvalDate,
            rejectionDate: appItem.rejectionDate,
            issue_date: appItem.issue_date,
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId,
          company_id: finalCompanyId,
        };
      });

    return filteredData;
  }, [applications.data, getallUserdata, user.user?.user_role, user.user?.company_id, user.user?.id]);

  // Apply search filter separately
  useEffect(() => {
    // If server-side search is active (debouncedSearchTerm is set), 
    // rely on the API results and skip strict local text filtering.
    if (debouncedSearchTerm && (searchTerm || searchQuery)) {
      setFilteredApplication(mergedAndFilteredData);
      setCurrentPage(1);
      return;
    }

    if (!searchQuery && !searchTerm) {
      setFilteredApplication(mergedAndFilteredData);
      return;
    }

    const term = searchQuery || searchTerm;
    const searchLower = term.toLowerCase();
    const searched = mergedAndFilteredData.filter((item) =>
      Object.values(item).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchLower)
      )
    );
    setFilteredApplication(searched);
    setCurrentPage(1);
  }, [searchQuery, searchTerm, mergedAndFilteredData, debouncedSearchTerm]);

  // Pagination data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredApplication, currentPage, itemsPerPage]);

  const getTotalPages = () => Math.ceil(filteredApplication?.length / itemsPerPage);

  // Download
  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;

    // Prevent multiple clicks
    if (downloadingIds.includes(applicationId)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      console.error("Download failed", error);
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken, downloadingIds]);

  // Create new application from previous data
  const handleCreateNewApplication = useCallback(async (row) => {
    try {
      const applicationId = row.applicationId || row.id;
      const userId = row.userId || row.user_id || user.user?.id;

      showSuccess('Fetching previous application data...');

      // Fetch the previous application data
      const applicationResponse = await dispatch(fetchApplication({
        id: applicationId,
        accessToken: authToken
      }));

      if (!applicationResponse.payload) {
        showError('Failed to fetch previous application data');
        return;
      }

      const previousApplication = applicationResponse.payload.application ||
        applicationResponse.payload.applications?.[0] ||
        applicationResponse.payload;

      if (!previousApplication) {
        showError('Application data not found');
        return;
      }

      // Parse json_data if it's a string, handle both string and object cases
      let jsonData = previousApplication.json_data;
      if (typeof jsonData === 'string') {
        // Check if it's a valid JSON string (not "[object Object]")
        if (jsonData.trim().startsWith('{') || jsonData.trim().startsWith('[')) {
          try {
            jsonData = JSON.parse(jsonData);
          } catch (e) {
            console.error('Error parsing json_data:', e);
            jsonData = {};
          }
        } else {
          // Handle cases like "[object Object]" - use empty object
          console.warn('Invalid json_data format, using empty object');
          jsonData = {};
        }
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        // Already an object, use as-is (no conversion needed)
      } else {
        // Fallback to empty object
        jsonData = {};
      }

      // Get attachment file names to pass to backend for copying
      let attachmentFiles = [];
      try {
        const attachments = await getAttachments({
          userId: userId,
          applicationId: applicationId,
          authToken: authToken
        });
        if (attachments && Array.isArray(attachments) && attachments?.length > 0) {
          // Pass file names as objects - backend will copy them from source application
          attachmentFiles = attachments.map(filename => ({ name: filename }));
        } else {
        }
      } catch (error) {
        console.error('Could not fetch attachments, continuing without them:', error);
      }

      const newApplicationResponse = await dispatch(postApplication({
        form: previousApplication.name || previousApplication.form,
        json_data: jsonData,
        name: previousApplication.name,
        category: previousApplication.category || previousApplication.categoryName,
        user_id: user.user.id,
        token: authToken,
        files: attachmentFiles,
        sourceApplicationId: applicationId,
        sourceUserId: userId,
        state: 'drafts',
        silent: true,
      }));

      if (!isThunkFulfilled(newApplicationResponse) || !newApplicationResponse.payload?.id) {
        notifyApiError(newApplicationResponse.payload, 'Failed to create new application');
        return;
      }

      const newApplicationId = newApplicationResponse.payload.id;
      const pdfRes = await dispatch(safePdf({
        htmlForm: previousApplication.htmlForm || previousApplication.html_form || "<div id=\"DocumentPDF\"></div>",
        user: { id: user.user.id },
        id: newApplicationId,
        token: authToken,
        silent: true,
      }));

      if (!isThunkFulfilled(pdfRes)) {
        notifyApiError(pdfRes.payload, 'Failed to save PDF');
        return;
      }

      dispatch(createNotification({
        user_id: user.user.id,
        applicationId: newApplicationId,
        applicationName: previousApplication.name,
        status: "drafts",
        accessToken: authToken,
        sendToCompanyAdmins: true
      }));

      notifyApiSuccess('New application created successfully!');
      navigate(`/app-renewal/${newApplicationId}`);
    } catch (error) {
      console.error('Error creating new application:', error);
      notifyApiError(error, 'Error creating new application');
    }
  }, [dispatch, authToken, user, navigate]);

  // Table Columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name || row.applicationName}>
                {(row.name || row.applicationName || "")?.length > 50
                  ? `${(row.name || row.applicationName || "").substring(0, 50)}...`
                  : (row.name || row.applicationName || 'N/A')}
              </strong>
              <span>{row.categoryName || row.category || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("submissionDate", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">
            {formatBahamasDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("approvalDate", {
        header: "Approved Date",
        cell: (info) => (
          <span className="dateText">
            {formatBahamasDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("issue_date", {
        header: "Registered Date",
        cell: (info) => (
          <span className="dateText">
            {formatBahamasDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        if (!downloadingIds.includes(row.applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: downloadingIds.includes(row.applicationId) ? 'none' : 'auto',
                        cursor: downloadingIds.includes(row.applicationId) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  <li>
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        handleCreateNewApplication(row);
                      }}
                      className="dropdown-item"
                    >
                      Create new appllication
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, currentPage, itemsPerPage, handleDownload, getallUserdata, handleCreateNewApplication]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Add loading state
  if (applications.fetchLoading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader" style={{ flexWrap: "wrap", gap: "10px" }}>
            <div className="boardHeadLeft">
              <h2>Approved Applications</h2>
            </div>
            <div
              className="boardHeadRight"
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <select onChange={handleSearchByChange} value={searchBy} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => {
                    let val = e.target.value;
                    if (searchBy === "applicationId") {
                      const digits = val.replace(/\D/g, "");
                      if (digits.length > 0) {
                        val = `GB-${digits}`;
                      } else {
                        val = "";
                      }
                    }
                    setSearchTerm(val);
                  }}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          {/* Table Head */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows?.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers?.length} style={{ textAlign: "center", padding: "20px" }}>
                      <p>No approved applications found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />

        </div>
      </div>
    </div>
  );
};

export default AppApprovedApp;